import * as styles from "./Tabs.css";

import { clsx } from "clsx";
import { type JSX, useEffect, useRef, useState } from "react";

import { Tabs as BaseTabs } from "@base-ui/react/tabs";

import { TabsContext, makeTabsContext } from "./Tabs.context";
import type { TabsProps } from "./Tabs.types";

const FLUSH_EPSILON_PX = 1;

export function Tabs({
  size,
  variant,
  orientation = "horizontal",
  className,
  ...props
}: TabsProps): JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const [flushEdges, setFlushEdges] = useState({
    flushStart: false,
    flushEnd: false,
  });

  useEffect(() => {
    const root = rootRef.current;
    const list = root?.querySelector<HTMLElement>('[data-slot="tabs-list"]');

    if (!root || !list) {
      return;
    }

    const updateFlushEdges = () => {
      const activeTab = list.querySelector<HTMLElement>(
        '[data-slot="tabs-tab"][data-active]',
      );

      if (!activeTab) {
        return;
      }

      const listRect = list.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();

      setFlushEdges(
        orientation === "horizontal"
          ? {
              flushStart: tabRect.left - listRect.left <= FLUSH_EPSILON_PX,
              flushEnd: listRect.right - tabRect.right <= FLUSH_EPSILON_PX,
            }
          : {
              flushStart: tabRect.top - listRect.top <= FLUSH_EPSILON_PX,
              flushEnd: listRect.bottom - tabRect.bottom <= FLUSH_EPSILON_PX,
            },
      );
    };

    updateFlushEdges();

    // The active tab can move without the list itself resizing (switching
    // to a same-width tab just translates the indicator), so we react to
    // `data-active` toggling rather than relying on ResizeObserver alone.

    const mutationObserver = new MutationObserver(updateFlushEdges);

    mutationObserver.observe(list, {
      attributes: true,
      attributeFilter: ["data-active"],
      subtree: true,
    });

    const resizeObserver = new ResizeObserver(updateFlushEdges);

    resizeObserver.observe(list);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [orientation]);

  return (
    <TabsContext.Provider
      value={makeTabsContext({ size, variant, orientation, ...flushEdges })}
    >
      <BaseTabs.Root
        ref={rootRef}
        data-slot="tabs"
        orientation={orientation}
        className={clsx(styles.tabs({ size, variant, orientation }), className)}
        {...props}
      />
    </TabsContext.Provider>
  );
}
